import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Palette, Download, Upload,
  RotateCcw, Sparkles,
  Type, Bell, Save, FileText, Sun, Moon
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAppStore, themePresets, defaultSettings, type ThemePreset } from '@/lib/store';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const presetNames: Record<ThemePreset, string> = {
  cream: 'Cream',
  sepia: 'Sepia',
  moonlit: 'Moonlit',
  paperwhite: 'Paperwhite',
  forest: 'Forest',
  midnight: 'Midnight',
  highcontrast: 'High Contrast',
};

export function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
  const {
    theme, setTheme,
    themeProfile, setThemePreset, setThemeProfile,
    settings, updateSettings, toggleQuietMode,
    backups, createBackup,
    exportToTxt, exportToMarkdown
  } = useAppStore();

  const [activeSection, setActiveSection] = useState<'theme' | 'editor' | 'backup'>('theme');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExportSettings = () => {
    const exportData = {
      themeProfile,
      settings,
      exportDate: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `authors-nexus-settings-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportSettings = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (data.themeProfile) setThemeProfile(data.themeProfile);
        if (data.settings) updateSettings(data.settings);
      } catch (err) {
        console.error('Failed to import settings:', err);
      }
    };
    reader.readAsText(file);
  };

  const handleResetSettings = () => {
    if (confirm('Reset all settings to defaults? This cannot be undone.')) {
      setThemeProfile({
        preset: 'cream',
        ...themePresets.cream,
        manuscriptFont: '"Crimson Text", Georgia, serif',
        manuscriptSize: 18,
        lineHeight: 1.8,
        letterSpacing: 0.01,
        bookTexture: false,
      });
      updateSettings(defaultSettings);
    }
  };

  const handleExportTxt = () => {
    const content = exportToTxt();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manuscript-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportMarkdown = () => {
    const content = exportToMarkdown();
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manuscript-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sections = [
    { id: 'theme', label: 'Theme', icon: Palette },
    { id: 'editor', label: 'Editor', icon: Type },
    { id: 'backup', label: 'Backup & Export', icon: Save },
  ] as const;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-lg z-50 shadow-2xl flex flex-col"
            style={{ backgroundColor: themeProfile.background }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: themeProfile.border }}
            >
              <h2
                className="text-lg font-semibold flex items-center gap-2"
                style={{ color: themeProfile.text }}
              >
                <Sparkles className="w-5 h-5" style={{ color: themeProfile.accent }} />
                Studio Panel
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:opacity-70 transition-opacity"
                style={{ color: themeProfile.text }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation */}
            <div
              className="flex gap-1 p-2 border-b overflow-x-auto"
              style={{ borderColor: themeProfile.border }}
            >
              {sections.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors',
                    activeSection === id && 'font-medium'
                  )}
                  style={{
                    backgroundColor: activeSection === id ? themeProfile.accent + '20' : 'transparent',
                    color: activeSection === id ? themeProfile.accent : themeProfile.text,
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4 space-y-6">
              {/* THEME SECTION */}
              {activeSection === 'theme' && (
                <div className="space-y-6">
                  {/* Primary Dark/Light Toggle */}
                  <div
                    className="p-4 rounded-xl flex items-center justify-between"
                    style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}` }}
                  >
                    <div>
                      <h3 className="font-medium flex items-center gap-2" style={{ color: themeProfile.text }}>
                        {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                        Display Mode
                      </h3>
                      <p className="text-xs opacity-70 mt-1" style={{ color: themeProfile.text }}>
                        Switch between Paperwhite and Dark modes
                      </p>
                    </div>
                    <button
                      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all shadow-sm active:scale-95",
                      )}
                      style={{
                        backgroundColor: themeProfile.accent,
                        color: themeProfile.background
                      }}
                    >
                      {theme === 'dark' ? 'Switch to Paperwhite' : 'Switch to Dark'}
                    </button>
                  </div>

                  {/* Presets */}

                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Theme Presets
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {(Object.keys(themePresets) as ThemePreset[]).map((preset) => {
                        const p = themePresets[preset];
                        const isSelected = themeProfile.preset === preset;
                        return (
                          <button
                            key={preset}
                            onClick={() => setThemePreset(preset)}
                            className={cn(
                              'p-3 rounded-xl border-2 transition-all text-left',
                              isSelected && 'ring-2'
                            )}
                            style={{
                              backgroundColor: p.background,
                              borderColor: isSelected ? p.accent : p.border,
                            }}
                          >
                            <div
                              className="w-full h-6 rounded mb-2"
                              style={{ backgroundColor: p.paperSurface }}
                            />
                            <p className="text-xs font-medium" style={{ color: p.text }}>
                              {presetNames[preset]}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Custom Colors */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Custom Colors
                    </h3>
                    <div className="space-y-3">
                      {[
                        { key: 'background', label: 'Background' },
                        { key: 'paperSurface', label: 'Paper Surface' },
                        { key: 'text', label: 'Text' },
                        { key: 'accent', label: 'Accent' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between">
                          <label className="text-sm" style={{ color: themeProfile.text }}>{label}</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={themeProfile[key as keyof typeof themeProfile] as string}
                              onChange={(e) => setThemeProfile({ [key]: e.target.value, preset: 'custom' })}
                              className="w-8 h-8 rounded cursor-pointer"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* EDITOR SECTION */}
              {activeSection === 'editor' && (
                <div className="space-y-6">
                  {/* Quiet Mode */}
                  <div
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}` }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium flex items-center gap-2" style={{ color: themeProfile.text }}>
                          <Bell className="w-4 h-4" style={{ color: themeProfile.accent }} />
                          Quiet Mode
                        </h3>
                        <p className="text-xs opacity-70 mt-1" style={{ color: themeProfile.text }}>
                          Disables non-essential prompts, toasts, and nudges
                        </p>
                      </div>
                      <button
                        onClick={toggleQuietMode}
                        className={cn(
                          'w-12 h-6 rounded-full transition-colors relative',
                          settings.quietMode ? 'bg-green-500' : 'bg-gray-400'
                        )}
                      >
                        <div
                          className={cn(
                            'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform',
                            settings.quietMode ? 'translate-x-6' : 'translate-x-0.5'
                          )}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Geography & Goals */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Goals & Progress
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-1">
                          <label className="text-xs" style={{ color: themeProfile.text }}>Daily Word Goal</label>
                          <span className="text-xs opacity-70" style={{ color: themeProfile.text }}>{settings.dailyWordGoal || 'None'} words</span>
                        </div>
                        <input
                          type="number"
                          step={100}
                          min={0}
                          value={settings.dailyWordGoal || ''}
                          onChange={(e) => updateSettings({ dailyWordGoal: Number(e.target.value) || null })}
                          className="w-full bg-transparent border-b outline-none text-sm py-1"
                          style={{ color: themeProfile.text, borderColor: themeProfile.border }}
                          placeholder="e.g. 1000"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Typography */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Typography
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-1">
                          <label className="text-xs" style={{ color: themeProfile.text }}>Font Size</label>
                          <span className="text-xs opacity-70" style={{ color: themeProfile.text }}>{themeProfile.manuscriptSize}px</span>
                        </div>
                        <input
                          type="range"
                          min={14}
                          max={24}
                          value={themeProfile.manuscriptSize}
                          onChange={(e) => setThemeProfile({ manuscriptSize: Number(e.target.value) })}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <label className="text-xs" style={{ color: themeProfile.text }}>Line Height</label>
                          <span className="text-xs opacity-70" style={{ color: themeProfile.text }}>{themeProfile.lineHeight.toFixed(1)}</span>
                        </div>
                        <input
                          type="range"
                          min={1.4}
                          max={2.2}
                          step={0.1}
                          value={themeProfile.lineHeight}
                          onChange={(e) => setThemeProfile({ lineHeight: Number(e.target.value) })}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Proofreading */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Proofreading
                    </h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.spellcheckEnabled}
                          onChange={() => updateSettings({ spellcheckEnabled: !settings.spellcheckEnabled })}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm" style={{ color: themeProfile.text }}>Spellcheck</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.grammarEnabled}
                          onChange={() => updateSettings({ grammarEnabled: !settings.grammarEnabled })}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm" style={{ color: themeProfile.text }}>Grammar suggestions</span>
                      </label>
                    </div>
                  </div>

                  {/* Psychology Features */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Writing Features
                    </h3>
                    <div className="space-y-2">
                      {[
                        { key: 'streakEnabled', label: 'Warm streak' },
                        { key: 'nextSentenceNudge', label: 'Next sentence nudge' },
                        { key: 'momentumMeterEnabled', label: 'Momentum meter' },
                        { key: 'wordCountArcEnabled', label: 'Progress arc (Wheel)' },
                        { key: 'fixedToolbarEnabled', label: 'Fixed toolbar (Always visible)' },
                        { key: 'floatingToolbarEnabled', label: 'Floating selection menu' },
                        { key: 'marginMenuEnabled', label: 'Margin insertion menu' },
                        { key: 'microDelightsEnabled', label: 'Micro delights' },
                        { key: 'breadcrumbsEnabled', label: 'End on a hook' },
                        { key: 'typewriterSoundsEnabled', label: 'Typewriter sounds' },
                        { key: 'typewriterScrollingEnabled', label: 'Fixed-line scrolling' },
                        { key: 'smartTypographyEnabled', label: 'Smart typography' },
                      ].map(({ key, label }) => (
                        <label key={key} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings[key as keyof typeof settings] as boolean}
                            onChange={() => updateSettings({ [key]: !settings[key as keyof typeof settings] })}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-sm" style={{ color: themeProfile.text }}>{label}</span>
                        </label>
                      ))}

                      {settings.typewriterSoundsEnabled && (
                        <div className="pt-2">
                          <label className="text-xs opacity-70 block mb-2" style={{ color: themeProfile.text }}>Sound Profile</label>
                          <div className="flex bg-black/5 dark:bg-white/5 p-1 rounded-lg">
                            <button
                              onClick={() => updateSettings({ soundProfile: 'mechanical' })}
                              className={cn(
                                "flex-1 px-3 py-1.5 text-xs rounded-md transition-all",
                                settings.soundProfile === 'mechanical'
                                  ? "bg-white dark:bg-zinc-800 shadow-sm font-medium"
                                  : "opacity-60 hover:opacity-100"
                              )}
                              style={{ color: themeProfile.text }}
                            >
                              Mechanical
                            </button>
                            <button
                              onClick={() => updateSettings({ soundProfile: 'typewriter' })}
                              className={cn(
                                "flex-1 px-3 py-1.5 text-xs rounded-md transition-all",
                                settings.soundProfile === 'typewriter'
                                  ? "bg-white dark:bg-zinc-800 shadow-sm font-medium"
                                  : "opacity-60 hover:opacity-100"
                              )}
                              style={{ color: themeProfile.text }}
                            >
                              Typewriter
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* BACKUP & EXPORT SECTION */}
              {activeSection === 'backup' && (
                <div className="space-y-6">
                  {/* Auto Backup */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Automatic Backup
                    </h3>
                    <label className="flex items-center gap-3 cursor-pointer mb-3">
                      <input
                        type="checkbox"
                        checked={settings.autoBackupEnabled}
                        onChange={() => updateSettings({ autoBackupEnabled: !settings.autoBackupEnabled })}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm" style={{ color: themeProfile.text }}>
                        Enable auto-backup every {settings.autoBackupInterval} minutes
                      </span>
                    </label>
                    <input
                      type="range"
                      min={5}
                      max={30}
                      step={5}
                      value={settings.autoBackupInterval}
                      onChange={(e) => updateSettings({ autoBackupInterval: Number(e.target.value) })}
                      className="w-full"
                      disabled={!settings.autoBackupEnabled}
                    />
                  </div>

                  {/* Manual Backup */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Manual Backup
                    </h3>
                    <button
                      onClick={createBackup}
                      className="w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: themeProfile.accent + '20',
                        color: themeProfile.accent,
                      }}
                    >
                      <Save className="w-4 h-4 inline mr-2" />
                      Create Backup Now
                    </button>
                    {backups.length > 0 && (
                      <p className="text-xs mt-2 opacity-70" style={{ color: themeProfile.text }}>
                        {backups.length} backup(s) stored
                      </p>
                    )}
                  </div>

                  {/* Export */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Export Manuscript
                    </h3>
                    <div className="space-y-2">
                      <button
                        onClick={handleExportTxt}
                        className="w-full py-2 px-4 rounded-lg text-sm transition-colors text-left"
                        style={{
                          backgroundColor: themeProfile.paperSurface,
                          border: `1px solid ${themeProfile.border}`,
                          color: themeProfile.text,
                        }}
                      >
                        <FileText className="w-4 h-4 inline mr-2" />
                        Export as .txt
                      </button>
                      <button
                        onClick={handleExportMarkdown}
                        className="w-full py-2 px-4 rounded-lg text-sm transition-colors text-left"
                        style={{
                          backgroundColor: themeProfile.paperSurface,
                          border: `1px solid ${themeProfile.border}`,
                          color: themeProfile.text,
                        }}
                      >
                        <FileText className="w-4 h-4 inline mr-2" />
                        Export as .md (Markdown)
                      </button>
                    </div>
                  </div>

                  {/* Settings Import/Export */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: themeProfile.text }}>
                      Settings
                    </h3>
                    <div className="space-y-2">
                      <button
                        onClick={handleExportSettings}
                        className="w-full py-2 px-4 rounded-lg text-sm transition-colors text-left"
                        style={{
                          backgroundColor: themeProfile.paperSurface,
                          border: `1px solid ${themeProfile.border}`,
                          color: themeProfile.text,
                        }}
                      >
                        <Download className="w-4 h-4 inline mr-2" />
                        Export Settings
                      </button>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full py-2 px-4 rounded-lg text-sm transition-colors text-left"
                        style={{
                          backgroundColor: themeProfile.paperSurface,
                          border: `1px solid ${themeProfile.border}`,
                          color: themeProfile.text,
                        }}
                      >
                        <Upload className="w-4 h-4 inline mr-2" />
                        Import Settings
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleImportSettings}
                        className="hidden"
                      />
                      <button
                        onClick={handleResetSettings}
                        className="w-full py-2 px-4 rounded-lg text-sm transition-colors text-left text-red-500"
                        style={{
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                        }}
                      >
                        <RotateCcw className="w-4 h-4 inline mr-2" />
                        Reset to Defaults
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default SettingsPanel;
